/**
 * Bundle 77 - Event rating badge.
 *
 * Small role-aware rating badge for use on event cards + event
 * detail pages. Hits `/api/events/{id}/rating` which returns
 * `{visible, avg, count, distribution}` OR `{visible: false, reason}`.
 * When `visible: false`, the badge renders NOTHING (per spec).
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import api from '@/utils/api';

const EventRatingBadge = ({ eventId, size = 'sm' }) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!eventId) return;
    let mounted = true;
    // Use silent401 so unauthenticated visitors do not trigger a login redirect.
    api.get(`/events/${eventId}/rating`, { silent401: true })
      .then((r) => { if (mounted) setState(r.data); })
      .catch(() => { if (mounted) setState({ visible: false }); });
    return () => { mounted = false; };
  }, [eventId]);
  if (!state || !state.visible) return null;
  const cls = size === 'lg'
    ? 'text-sm gap-1.5 px-2.5 py-1'
    : 'text-xs gap-1 px-2 py-0.5';
  return (
    <span
      className={`inline-flex items-center rounded-full bg-secondary-50 text-secondary-800 border border-secondary-200 ${cls}`}
      data-testid={`event-rating-badge-${eventId}`}
      title={`Distribution: ${JSON.stringify(state.distribution || {})}`}
    >
      <Star className="h-3 w-3 fill-secondary-500 text-secondary-500" />
      <span className="font-semibold" data-testid={`event-rating-avg-${eventId}`}>
        {typeof state.avg === 'number' ? state.avg.toFixed(1) : '-'}
      </span>
      <span aria-hidden="true">*</span>
      <span data-testid={`event-rating-count-${eventId}`}>
        {state.count} attendee {state.count === 1 ? 'rating' : 'ratings'}
      </span>
    </span>
  );
};

export default EventRatingBadge;
